import moment from "moment";
import { SessionRow, sessionEnd, sessionStart, sessionsTotalHours, sortSessionRows } from "../DeclarationCourse/SessionList";
import { clock, dayLabel, hours, plural } from "./format";
import { DeclarationV2State } from "./types";

/** Nombre d'heures de la déclaration (séances ou cours unique). */
export const declarationHours = (state: DeclarationV2State): number =>
    state.courseType === "multiple" ? sessionsTotalHours(state.sessions) : state.single.duration;

/** Début et fin de la période couverte (ISO), pour l'envoi et les scénarios de déplacement. */
export const declarationPeriod = (state: DeclarationV2State): { start: string | null; end: string | null } => {
    if (state.courseType === "multiple") {
        const starts = state.sessions.map(sessionStart).filter(Boolean) as moment.Moment[];
        const ends = state.sessions.map(sessionEnd).filter(Boolean) as moment.Moment[];
        if (starts.length === 0) return { start: null, end: null };
        return { start: moment.min(starts).toISOString(), end: moment.max(ends).toISOString() };
    }
    const row: SessionRow = { key: "single", date: state.single.date, startTime: state.single.startTime, duration: state.single.duration };
    const start = sessionStart(row);
    const end = sessionEnd(row);
    return { start: start ? start.toISOString() : null, end: end ? end.toISOString() : null };
};

/** « Jeudis 15, 22 et 29 octobre, 16h00, 1 h 30 » ou « 3 séances, 4 h 30 » ou « Vendredi 30 octobre, 14h00 à 15h30 ». */
export const planningSummary = (state: DeclarationV2State): string => {
    if (state.courseType === "multiple") {
        const rows = sortSessionRows(state.sessions);
        const starts = rows.map(sessionStart).filter(Boolean) as moment.Moment[];
        if (starts.length === 0) return "";
        const sameWeekday = starts.every((s) => s.isoWeekday() === starts[0].isoWeekday());
        const sameTime = starts.every((s) => s.format("HH:mm") === starts[0].format("HH:mm"));
        const sameDuration = rows.every((r) => r.duration === rows[0].duration);
        const sameMonth = starts.every((s) => s.format("YYYY-MM") === starts[0].format("YYYY-MM"));
        if (sameWeekday && sameTime && sameDuration && sameMonth && starts.length <= 6) {
            const weekday = starts[0].locale("fr").format("dddd");
            const days = starts.map((s) => s.date());
            const list = days.length === 1 ? String(days[0]) : `${days.slice(0, -1).join(", ")} et ${days[days.length - 1]}`;
            const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
            return `${cap}s ${list} ${starts[0].locale("fr").format("MMMM")}, ${clock(starts[0])}, ${hours(rows[0].duration)}`;
        }
        return `${plural(rows.length, "séance")}, ${hours(sessionsTotalHours(rows))}`;
    }
    const { start, end } = declarationPeriod(state);
    if (!start || !end) return "";
    return `${dayLabel(start)}, ${clock(start)} à ${clock(end)}`;
};

/** « Plusieurs cours de mathématiques, 3 séances, 4 h 30 au total » */
export const headlineSummary = (state: DeclarationV2State, typeLabel: string, subjectLabel: string | null): string => {
    const subject = subjectLabel ? ` de ${subjectLabel.toLowerCase()}` : "";
    if (state.courseType === "multiple") {
        return `${typeLabel}${subject}, ${plural(state.sessions.length, "séance")}, ${hours(declarationHours(state))} au total`;
    }
    return `${typeLabel}${subject}, ${hours(state.single.duration)}`;
};
